import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Briefcase, Loader2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";

const HERO_BG = "https://images.unsplash.com/photo-1763652387504-c9ed66d86d02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJsdXJyZWQlMjBncmFkaWVudCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc4NDExMzU2fDA&ixlib=rb-4.1.0&q=85";
const TEAM = "https://images.unsplash.com/photo-1698047682129-c3e217ac08b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxvZmZpY2UlMjB0ZWFtJTIwd29ya2luZyUyMHRvZ2V0aGVyfGVufDB8fHx8MTc3ODQxMTM1Mnww&ixlib=rb-4.1.0&q=85";

export default function Careers() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openJob, setOpenJob] = useState(null);

    useEffect(() => {
        api.get("/public/jobs")
            .then(({ data }) => setJobs(data.items || []))
            .catch(() => toast.error("Could not load openings"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-background">
            {/* Hero */}
            <section className="relative h-[60vh] min-h-[480px] overflow-hidden">
                <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background" />
                <div className="relative z-10 h-full flex flex-col">
                    <header className="px-6 lg:px-12 h-20 flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-2 text-white">
                            <div className="w-9 h-9 rounded-xl bg-white grid place-items-center shadow-[0_0_20px_rgba(255,87,51,0.15)] overflow-hidden p-1">
                                <img src="/logo.png" alt="ParshHR Logo" className="w-full h-full object-contain" />
                            </div>
                            <span className="font-heading font-bold text-xl">ParshHR</span>
                        </Link>
                        <nav className="hidden md:flex items-center gap-8 text-sm text-white/80">
                            <Link to="/" className="hover:text-white">Home</Link>
                            <Link to="/login" className="hover:text-white">Sign in</Link>
                        </nav>
                    </header>
                    <div className="flex-1 grid place-items-center text-center px-6">
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
                            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-accent mb-3">Join us</div>
                            <h1 className="font-heading text-5xl sm:text-6xl font-bold tracking-tighter leading-[1.05] text-white">
                                Build the future of work — with us.
                            </h1>
                            <p className="text-white/80 mt-5 max-w-xl mx-auto">
                                We're a small team obsessed with crafting tools that respect humans. If that sounds like you, we'd love to talk.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Jobs */}
            <section className="px-6 lg:px-12 py-20 max-w-6xl mx-auto">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-2">Open roles</div>
                        <h2 className="font-heading text-4xl font-bold tracking-tight">Find your spot</h2>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
                ) : jobs.length === 0 ? (
                    <div className="glass rounded-2xl p-12 text-center">
                        <div className="text-muted-foreground">No open roles right now — check back soon!</div>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {jobs.map((j, i) => (
                            <motion.div
                                key={j.id}
                                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                                className="glass glass-hover rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                                data-testid={`job-card-${j.id}`}
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                                        <Briefcase className="w-3 h-3" /> {j.department || "General"} · {j.employment_type?.replace("_", " ")}
                                    </div>
                                    <h3 className="font-heading text-xl font-semibold">{j.title}</h3>
                                    <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{j.description}</div>
                                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}</span>
                                        {j.salary_range && <span>· {j.salary_range}</span>}
                                        {j.experience && <span>· {j.experience}</span>}
                                    </div>
                                </div>
                                <Button onClick={() => setOpenJob(j)} className="rounded-full" data-testid={`apply-btn-${j.id}`}>
                                    Apply <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </motion.div>
                        ))}
                    </div>
                )}
            </section>

            {/* Culture */}
            <section className="px-6 lg:px-12 py-20 bg-black/[0.02] dark:bg-white/[0.02]">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-3">Our culture</div>
                        <h2 className="font-heading text-4xl font-bold tracking-tight">A small team, big ambitions.</h2>
                        <p className="text-muted-foreground mt-4">
                            We move fast, take ownership and care deeply about craft. We believe great work happens when people feel safe, seen and stretched.
                        </p>
                    </div>
                    <div className="rounded-3xl overflow-hidden h-[360px]">
                        <img src={TEAM} alt="team" className="w-full h-full object-cover" />
                    </div>
                </div>
            </section>

            <ApplyDialog job={openJob} onClose={() => setOpenJob(null)} />

            <footer className="px-6 lg:px-12 py-8 border-t border-black/5 dark:border-white/10 text-sm text-muted-foreground text-center">
                © {new Date().getFullYear()} ParshHR
            </footer>
        </div>
    );
}

function ApplyDialog({ job, onClose }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "", experience: "" });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (job) {
            setForm({ name: "", email: "", phone: "", cover_letter: "", experience: "" });
            setSubmitted(false);
        }
    }, [job]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/public/apply", { ...form, job_id: job.id });
            setSubmitted(true);
            toast.success("Application submitted!");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl" data-testid="apply-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Apply for {job?.title}</DialogTitle>
                </DialogHeader>
                {submitted ? (
                    <div className="py-8 text-center">
                        <div className="text-5xl mb-3">🎉</div>
                        <h3 className="font-heading text-xl font-semibold">Thanks for applying!</h3>
                        <p className="text-muted-foreground text-sm mt-2">We've sent a confirmation to {form.email}. Our team will reach out soon.</p>
                        <Button onClick={onClose} className="mt-6 rounded-full">Close</Button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Full name</Label>
                                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="apply-name" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="apply-email" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Phone</Label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="apply-phone" />
                            </div>
                            <div>
                                <Label>Experience</Label>
                                <Input placeholder="e.g. 3 yrs" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="mt-1.5" data-testid="apply-experience" />
                            </div>
                        </div>
                        <div>
                            <Label>Cover letter</Label>
                            <Textarea rows={5} placeholder="Tell us why you're a great fit." value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} className="mt-1.5" data-testid="apply-cover-letter" />
                        </div>
                        <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="apply-submit">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit application"}
                        </Button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
